import {
  type IAnalyticsRuntimeConfig,
  resolveAnalyticsConfig,
} from "../product-analytics/product-analytics.config";
import type { IAppConfigResponse } from "../types/app-config";

// NOTE: NSAI-1606 — the web app's per-deployment settings come from the
// server's /app-config rather than from values
// baked into the bundle at build time. The build-time value stays only as the
// answer for the moments the server has not given one: before /app-config
// resolves, and against a server whose /app-config cannot be read. That
// keeps the first frame stable and an image newer than its server working.

export interface IResolveIsOnPremiseProps {
  buildValue: boolean;
  appConfig?: IAppConfigResponse;
}

export const resolveIsOnPremise = ({
  appConfig,
  buildValue,
}: IResolveIsOnPremiseProps): boolean =>
  appConfig?.deployment.isOnPremise ?? buildValue;

export interface IResolveAnalyticsSettingsProps {
  buildIsOnPremise: boolean;
  appConfig?: IAppConfigResponse;
  buildKey?: string;
  buildHost?: string;
  runtimeConfig?: IAnalyticsRuntimeConfig;
}

export interface IAnalyticsSettings {
  apiKey?: string;
  host?: string;
}

// NOTE: An on-premise deployment sends nothing, whatever the bundle or the
// deploy carries, so a mis-built image cannot send a customer's telemetry out
// of their network; when /app-config cannot be read, the build's own
// on-premise value decides, as it does for resolveIsOnPremise.
//
// Otherwise the first COMPLETE key + host pair wins, each taken whole, never
// mixed (NSAI-4623):
//   1. the runtime pair a deploy writes into /posthog-init.js — it knows the
//      browser-facing address (the same-origin `/m` proxy) of the PostHog that
//      deploy runs;
//   2. the server's pair from /app-config (a `phc_` key and an https host);
//   3. the build pair baked into the image.
// None complete → no settings, and the client stays off.
export const resolveAnalyticsSettings = ({
  buildIsOnPremise,
  appConfig,
  buildKey,
  buildHost,
  runtimeConfig,
}: IResolveAnalyticsSettingsProps): IAnalyticsSettings => {
  if (resolveIsOnPremise({ buildValue: buildIsOnPremise, appConfig })) {
    return {};
  }

  return (
    resolveAnalyticsConfig({ runtimeConfig }) ??
    resolveAnalyticsConfig({
      runtimeConfig: {
        key: appConfig?.analytics.posthogKey,
        host: appConfig?.analytics.posthogHost,
      },
    }) ??
    resolveAnalyticsConfig({ buildKey, buildHost }) ??
    {}
  );
};
